/**
 * Modern IBAN validation utilities
 * Separated from legacy iban.ts to maintain clean architecture
 */

// Country code to IBAN length mapping (ISO 13616)
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, // Andorra
  AE: 23, // United Arab Emirates
  AL: 28, // Albania
  AT: 20, // Austria
  AZ: 28, // Azerbaijan
  BA: 20, // Bosnia and Herzegovina
  BE: 16, // Belgium
  BG: 22, // Bulgaria
  BH: 22, // Bahrain
  BR: 29, // Brazil
  BY: 28, // Belarus
  CH: 21, // Switzerland
  CR: 22, // Costa Rica
  CY: 28, // Cyprus
  CZ: 24, // Czech Republic
  DE: 22, // Germany
  DK: 18, // Denmark
  DO: 28, // Dominican Republic
  EE: 20, // Estonia
  EG: 29, // Egypt
  ES: 24, // Spain
  FI: 18, // Finland
  FO: 18, // Faroe Islands
  FR: 27, // France
  GB: 22, // United Kingdom
  GE: 22, // Georgia
  GI: 23, // Gibraltar
  GL: 18, // Greenland
  GR: 27, // Greece
  GT: 28, // Guatemala
  HR: 21, // Croatia
  HU: 28, // Hungary
  IE: 22, // Ireland
  IL: 23, // Israel
  IS: 26, // Iceland
  IT: 27, // Italy
  JO: 30, // Jordan
  KW: 30, // Kuwait
  KZ: 20, // Kazakhstan
  LB: 28, // Lebanon
  LC: 32, // Saint Lucia
  LI: 21, // Liechtenstein
  LT: 20, // Lithuania
  LU: 20, // Luxembourg
  LV: 21, // Latvia
  MC: 27, // Monaco
  MD: 24, // Moldova
  ME: 22, // Montenegro
  MK: 19, // North Macedonia
  MR: 27, // Mauritania
  MT: 31, // Malta
  MU: 30, // Mauritius
  NL: 18, // Netherlands
  NO: 15, // Norway
  PK: 24, // Pakistan
  PL: 28, // Poland
  PS: 29, // Palestine
  PT: 25, // Portugal
  QA: 29, // Qatar
  RO: 24, // Romania
  RS: 22, // Serbia
  SA: 24, // Saudi Arabia
  SE: 24, // Sweden
  SI: 19, // Slovenia
  SK: 24, // Slovakia
  SM: 27, // San Marino
  TN: 24, // Tunisia
  TR: 26, // Turkey
  UA: 29, // Ukraine
  VG: 24, // British Virgin Islands
  XK: 20, // Kosovo
};

/**
 * Core validation error codes that can be produced by the IBAN validation utility
 */
export const CORE_VALIDATION_ERROR_CODES = [
  "INVALID_INPUT", // IBAN is required and must be a string
  "FORMAT_INVALID_LENGTH", // IBAN too short or too long
  "FORMAT_INVALID_STRUCTURE", // Invalid country code, check digits, or contains invalid characters
  "FORMAT_INVALID_CHECK_DIGITS", // Check digits must be between 02 and 98
  "COUNTRY_NOT_SUPPORTED", // Country code not in ISO registry
  "COUNTRY_NOT_ALLOWED", // Country code not in allowed list
  "COUNTRY_INVALID_LENGTH", // IBAN length doesn't match expected length for country
  "CHECKSUM_FAILED", // MOD-97 validation failed
] as const;

export type CoreValidationErrorCode = (typeof CORE_VALIDATION_ERROR_CODES)[number];

export interface ValidationError<TCode extends string = string> {
  code: TCode;
  message: string; // Pour debug/logs backend
  clientMessage: string; // Pour affichage utilisateur mobile
}

// Type alias for core validation errors (used throughout this file)
export type CoreValidationError = ValidationError<CoreValidationErrorCode>;

export type IbanValidationResult =
  | {
      isValid: false;
      errors: CoreValidationError[];
      country?: string;
      normalizedIban?: string;
    }
  | {
      isValid: true;
      country: string;
      normalizedIban: string;
    };

export interface IbanValidationOptions {
  allowedCountries?: string[]; // Filter specific countries
}

/**
 * Normalize IBAN: remove spaces, special chars, convert to uppercase
 */
export function normalizeIban(iban: string): string {
  return iban.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Validate basic IBAN format structure
 */
export function validateIbanFormat(iban: string): {
  isValid: boolean;
  errors: CoreValidationError[];
} {
  const errors: CoreValidationError[] = [];
  const normalized = normalizeIban(iban);

  // Check minimum length
  if (normalized.length < 15) {
    errors.push({
      code: "FORMAT_INVALID_LENGTH",
      message: "IBAN too short (minimum 15 characters)",
      clientMessage: "Le numéro IBAN est trop court",
    });
  }

  // Check maximum length
  if (normalized.length > 34) {
    errors.push({
      code: "FORMAT_INVALID_LENGTH",
      message: "IBAN too long (maximum 34 characters)",
      clientMessage: "Le numéro IBAN est trop long",
    });
  }

  // Check country code (first 2 chars must be letters)
  if (!/^[A-Z]{2}/.test(normalized)) {
    errors.push({
      code: "FORMAT_INVALID_STRUCTURE",
      message: "Invalid country code (first 2 characters must be letters)",
      clientMessage: "Le format de l'IBAN n'est pas valide",
    });
  }

  // Check check digits (chars 3-4 must be digits)
  if (normalized.length >= 4 && !/^[A-Z]{2}[0-9]{2}/.test(normalized)) {
    errors.push({
      code: "FORMAT_INVALID_STRUCTURE",
      message: "Invalid check digits (characters 3-4 must be digits)",
      clientMessage: "Le format de l'IBAN n'est pas valide",
    });
  }

  // Check check digits range (02-98, never 00, 01, 99)
  if (normalized.length >= 4) {
    const checkDigits = parseInt(normalized.substring(2, 4), 10);
    if (checkDigits < 2 || checkDigits > 98) {
      errors.push({
        code: "FORMAT_INVALID_CHECK_DIGITS",
        message: "Check digits must be between 02 and 98",
        clientMessage: "Le format de l'IBAN n'est pas valide",
      });
    }
  }

  // Check only alphanumeric characters
  if (!/^[A-Z0-9]+$/.test(normalized)) {
    errors.push({
      code: "FORMAT_INVALID_STRUCTURE",
      message: "IBAN contains invalid characters",
      clientMessage: "Le format de l'IBAN n'est pas valide",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate IBAN country code and length
 */
export function validateIbanCountry(
  iban: string,
  allowedCountries?: string[]
): { isValid: boolean; errors: CoreValidationError[]; country?: string } {
  const errors: CoreValidationError[] = [];
  const normalized = normalizeIban(iban);
  const country = normalized.substring(0, 2);

  // Check if country code exists in our registry
  if (!IBAN_LENGTHS[country]) {
    errors.push({
      code: "COUNTRY_NOT_SUPPORTED",
      message: `Unsupported country code: ${country}`,
      clientMessage: "Ce pays n'est pas supporté",
    });
    return { isValid: false, errors };
  }

  // Check if country is in allowed list
  if (allowedCountries && !allowedCountries.includes(country)) {
    errors.push({
      code: "COUNTRY_NOT_ALLOWED",
      message: `Country ${country} not allowed`,
      clientMessage: "Ce pays n'est pas autorisé",
    });
  }

  // Check country-specific length
  const expectedLength = IBAN_LENGTHS[country];
  if (normalized.length !== expectedLength) {
    errors.push({
      code: "COUNTRY_INVALID_LENGTH",
      message: `Invalid length for ${country}: expected ${expectedLength}, got ${normalized.length}`,
      clientMessage: "La longueur ne correspond pas au pays",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    country,
  };
}

/**
 * Validate IBAN checksum using MOD-97 algorithm
 */
export function validateIbanChecksum(iban: string): {
  isValid: boolean;
  errors: CoreValidationError[];
} {
  const errors: CoreValidationError[] = [];
  const normalized = normalizeIban(iban);

  // Move first 4 characters to the end
  const rearranged = normalized.substring(4) + normalized.substring(0, 4);

  // Replace letters with numbers (A=10, B=11, ..., Z=35)
  const numericString = rearranged.replace(/[A-Z]/g, (letter) => {
    return (letter.charCodeAt(0) - 55).toString();
  });

  // Calculate MOD 97 (BigInt should never throw for valid numeric strings)
  const remainder = BigInt(numericString) % BigInt(97);

  if (remainder !== BigInt(1)) {
    errors.push({
      code: "CHECKSUM_FAILED",
      message: "Invalid checksum (MOD-97 validation failed)",
      clientMessage: "Le numéro IBAN contient une erreur",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Complete IBAN validation with all checks
 *
 * Validates an IBAN through a comprehensive 3-step process:
 *
 * 1. **Format validation**:
 *    - Normalizes IBAN (removes spaces, converts to uppercase)
 *    - Checks length (15-34 characters)
 *    - Validates structure (2 letters + 2 digits + alphanumeric)
 *    - Ensures check digits are in valid range (02-98)
 *
 * 2. **Country validation**:
 *    - Verifies country code exists in ISO registry
 *    - Checks country-specific IBAN length
 *    - Optionally filters allowed countries
 *
 * 3. **Checksum validation**:
 *    - Applies MOD-97 algorithm (ISO 13616 standard)
 *    - Rearranges IBAN (move first 4 chars to end)
 *    - Converts letters to numbers (A=10, B=11, ..., Z=35)
 *    - Calculates modulo 97, must equal 1
 *
 * @param iban - The IBAN string to validate
 * @param options - Validation options
 * @param options.allowedCountries - Array of allowed country codes (e.g., ['FR', 'DE'])
 *
 * @returns Validation result with status, errors, country and normalized IBAN
 *
 * @example
 * ```typescript
 * // Valid French IBAN
 * const result = validateIban('FR76 3000 6000 0112 3456 7890 189');
 * console.log(result.isValid); // true
 * console.log(result.country); // 'FR'
 * console.log(result.normalizedIban); // 'FR7630006000011234567890189'
 *
 * // Invalid IBAN with detailed errors
 * const result2 = validateIban('FR76300060001');
 * console.log(result2.isValid); // false
 * console.log(result2.errors); // ['Invalid length for FR: expected 27, got 13']
 *
 * // Restrict to specific countries
 * const result3 = validateIban('DE89370400440532013000', { allowedCountries: ['FR'] });
 * console.log(result3.errors); // ['Country DE not allowed']
 * ```
 */
export function validateIban(iban: string, options: IbanValidationOptions = {}): IbanValidationResult {
  const { allowedCountries } = options;

  if (!iban || typeof iban !== "string") {
    return {
      isValid: false,
      errors: [
        {
          code: "INVALID_INPUT",
          message: "IBAN is required and must be a string",
          clientMessage: "Données d'entrée invalides",
        },
      ],
    };
  }

  const normalized = normalizeIban(iban);
  const allErrors: CoreValidationError[] = [];

  // 1. Format validation
  const formatResult = validateIbanFormat(iban);
  allErrors.push(...formatResult.errors);

  // Early return if format is invalid and we can't proceed
  if (!formatResult.isValid) {
    return {
      isValid: false,
      errors: allErrors,
    };
  }

  // 2. Country validation
  const countryResult = validateIbanCountry(iban, allowedCountries);
  allErrors.push(...countryResult.errors);

  // 3. Checksum validation
  const checksumResult = validateIbanChecksum(iban);
  allErrors.push(...checksumResult.errors);

  if (allErrors.length === 0 && countryResult.country) {
    return {
      isValid: true,
      country: countryResult.country,
      normalizedIban: normalized,
    };
  }

  return {
    isValid: false,
    errors: allErrors,
    country: countryResult.country,
    normalizedIban: normalized,
  };
}

/**
 * Get expected length for a country code
 */
export function getCountryIbanLength(countryCode: string): number | null {
  return IBAN_LENGTHS[countryCode.toUpperCase()] || null;
}

const IBAN_GROUP_SIZE = 4;

/**
 * Splits an IBAN into groups of 4 characters for display and copy.
 * Normalizes the input first so spaced or mixed-case values still group correctly.
 *
 * @example
 * splitIbanIntoGroups("FR7630006000011234567890189") // ["FR76", "3000", "6000", "0112", "3456", "7890", "189"]
 * splitIbanIntoGroups("FR76 3000 6000 0112 3456 7890 189") // ["FR76", "3000", "6000", "0112", "3456", "7890", "189"]
 * splitIbanIntoGroups("") // []
 */
export function splitIbanIntoGroups(iban: string | undefined): string[] {
  if (!iban) {
    return [];
  }

  const normalized = normalizeIban(iban);
  if (!normalized) {
    return [];
  }

  const groups: string[] = [];
  for (let index = 0; index < normalized.length; index += IBAN_GROUP_SIZE) {
    groups.push(normalized.slice(index, index + IBAN_GROUP_SIZE));
  }
  return groups;
}

/**
 * Formats an IBAN for display with space every 4 characters
 *
 * @param iban - The IBAN to format (spaces and casing are ignored)
 * @returns Formatted IBAN (e.g., "FR76 3000 6000 0112 3456 7890 189") or empty string if invalid
 *
 * @example
 * formatIban("FR7630006000011234567890189") // "FR76 3000 6000 0112 3456 7890 189"
 * formatIban("DE89370400440532013000") // "DE89 3704 0044 0532 0130 00"
 * formatIban("") // ""
 */
export function formatIban(iban: string | undefined): string {
  return splitIbanIntoGroups(iban).join(" ");
}

/**
 * Masks an IBAN for safe logging, showing only the country code and last 4 characters
 * Assumes the input is already normalized (uppercase, no spaces)
 *
 * @param iban - The normalized IBAN to mask
 * @returns Masked IBAN (e.g., "FR76*******************0189") or masked string if too short
 *
 * @example
 * maskIban("FR7630006000011234567890189") // "FR76*******************0189"
 * maskIban("DE89370400440532013000") // "DE89**************3000"
 * maskIban("FR76") // "****"
 * maskIban("") // ""
 */
export function maskIban(iban: string | undefined): string {
  if (!iban || iban.length === 0) {
    return "";
  }

  // If IBAN is too short, mask it entirely
  if (iban.length < 8) {
    return "*".repeat(iban.length);
  }

  // Show first 4 chars (country code + check digits) and last 4 chars
  const firstChars = iban.slice(0, 4);
  const lastChars = iban.slice(-4);
  const maskedLength = iban.length - 8; // Total length minus visible chars
  const mask = "*".repeat(maskedLength);

  return `${firstChars}${mask}${lastChars}`;
}
